/**
 * SPEC §11 – Mijn uren: weekstaat, afspraken uit de agenda en de sleutel voor de Outlook-invoegtoepassing.
 */
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { addDagen, fmtDag, fmtDuur, isoWeek, leesDuur, maandagVan, vandaag } from '@ep/core';
import { api } from '../lib/api';
import { useActie, useData, useIk, useMag } from '../lib/data';
import { toast } from '../ui/toast';
import { Badge, Laden, Modal, Tabs, VerwijderKnop } from '../ui/ui';
import { DoelZoeker, SOORT_LABEL, TijdToevoegen, useVerversUren, type Doel } from '../ui/Uren';

interface Regel { id: string; datum: string; minuten: number; omschrijving: string | null; facturabel: boolean; bron: string; eind: string | null; goedgekeurdOp: string | null; sleutel: string }
interface Week { maandag: string; dagen: string[]; engineer: { id: string; naam: string }; regels: Regel[]; rijen: (Doel & { sleutel: string })[]; beschikbaar: Record<string, number>; status: 'leeg' | 'open' | 'deels' | 'goedgekeurd' }

const BRON: Record<string, string> = { TIMER: 'timer', HANDMATIG: '+ tijd', WEEKSTAAT: 'weekstaat', AGENDA: 'agenda' };

export function MijnUren() {
  const [zp, setZp] = useSearchParams();
  const ik = useIk().data;
  const mag = useMag();
  const [tab, setTab] = useState<'week' | 'agenda' | 'outlook'>((zp.get('tab') as 'week') ?? 'week');
  const maandag = maandagVan(zp.get('week') ?? vandaag());
  const engineerId = zp.get('engineer') ?? undefined;
  const zet = (k: string, v?: string) => {
    const n = new URLSearchParams(zp);
    if (v) n.set(k, v);
    else n.delete(k);
    setZp(n, { replace: true });
  };
  if (!ik) return <Laden />;
  if (!ik.eid && !engineerId) {
    return <div className="pagina"><div className="paneel leeg">Je gebruiker is niet gekoppeld aan een engineer, dus je schrijft zelf geen uren. Bekijk de uren van anderen bij <b>Beheer › Urenrapport</b>.</div></div>;
  }
  return (
    <div className="pagina kolom" style={{ gap: 16 }}>
      <div className="paneel kolom" style={{ gap: 12 }}>
        <div className="rij wrap">
          <h1 className="groei">Mijn uren</h1>
          {mag('uren.alle') && <EngineerKiezer waarde={engineerId} onChange={(v) => zet('engineer', v)} />}
          <button className="knop" onClick={() => zet('week', addDagen(maandag, -7))}>‹</button>
          <button className="knop" onClick={() => zet('week', undefined)}>Deze week</button>
          <button className="knop" onClick={() => zet('week', addDagen(maandag, 7))}>›</button>
          <b>Week {isoWeek(maandag)}</b>
        </div>
        <Tabs waarde={tab} onChange={(t) => { setTab(t); zet('tab', t === 'week' ? undefined : t); }} opties={engineerId && engineerId !== ik.eid ? [['week', 'Weekstaat']] : [['week', 'Weekstaat'], ['agenda', 'Uit je agenda'], ['outlook', 'Outlook-invoegtoepassing']]} />
        {tab === 'week' && <Weekstaat maandag={maandag} engineerId={engineerId} />}
        {tab === 'agenda' && <UitAgenda maandag={maandag} />}
        {tab === 'outlook' && <OutlookSleutels />}
      </div>
    </div>
  );
}

function EngineerKiezer({ waarde, onChange }: { waarde?: string; onChange: (v?: string) => void }) {
  const { data: d } = useData();
  const ik = useIk().data;
  return (
    <select value={waarde ?? ''} onChange={(e) => onChange(e.target.value || undefined)} style={{ width: 'auto' }}>
      <option value="">{ik?.eid ? 'Mijn eigen uren' : '– kies een engineer –'}</option>
      {d?.engineers.filter((e) => e.actief !== false && e.id !== ik?.eid).map((e) => <option key={e.id} value={e.id}>{e.naam}</option>)}
    </select>
  );
}

/** SPEC §11.2 – weekstaat: rijen = doelen, kolommen = ma–vr, cellen = uren (u:mm). */
function Weekstaat({ maandag, engineerId }: { maandag: string; engineerId?: string }) {
  const ververs = useVerversUren();
  const mag = useMag();
  const q = useQuery({ queryKey: ['week', maandag, engineerId], queryFn: () => api.get<Week>(`/api/tijd/week?maandag=${maandag}${engineerId ? `&engineerId=${engineerId}` : ''}`) });
  const [extra, setExtra] = useState<(Doel & { sleutel: string })[]>([]);
  const [kies, setKies] = useState(false);
  const [toevoegen, setToevoegen] = useState(false);
  useEffect(() => setExtra([]), [maandag, engineerId]);
  const verwijder = useActie((id: string) => api.del(`/api/tijd/regels/${id}`), { succes: 'Regel verwijderd', opNa: ververs });
  const factuur = useActie((r: Regel) => api.put(`/api/tijd/regels/${r.id}`, { facturabel: !r.facturabel }), { opNa: ververs });
  if (!q.data) return <Laden />;
  const w = q.data;
  const vast = w.status === 'goedgekeurd' && !mag('uren.alle');
  const rijen = [...w.rijen, ...extra.filter((x) => !w.rijen.some((r) => r.sleutel === x.sleutel))];
  const som = (sl: string | null, d: string | null) => w.regels.filter((r) => (!sl || r.sleutel === sl) && (!d || r.datum === d)).reduce((t, r) => t + r.minuten, 0);
  const totaal = som(null, null);
  const beschikbaar = w.dagen.reduce((t, d) => t + (w.beschikbaar[d] ?? 0), 0) * 60;
  const opslaan = async (rij: Doel, datum: string, tekst: string) => {
    const m = leesDuur(tekst);
    if (m == null) return toast('Ongeldige duur; gebruik bijv. 1,5 of 1:30', 'fout');
    if (m === som(`${rij.soort}:${rij.id}`, datum)) return;
    try {
      await api.put('/api/tijd/cel', { doel: { soort: rij.soort, id: rij.id }, datum, minuten: m, engineerId });
      ververs();
    } catch (e) {
      toast((e as Error).message, 'fout');
      ververs();
    }
  };
  return (
    <div className="kolom" style={{ gap: 12 }}>
      <div className="rij wrap">
        <span className="groei">
          {w.engineer.naam} · <b>{fmtDuur(totaal)}</b> van {fmtDuur(beschikbaar)} uur geschreven{' '}
          {w.status === 'goedgekeurd' ? <Badge toon="groen">goedgekeurd</Badge> : w.status === 'deels' ? <Badge toon="oranje">deels goedgekeurd</Badge> : null}
        </span>
        {!vast && <button className="knop" onClick={() => setKies(true)}>+ Rij</button>}
        {!vast && <button className="knop merk" onClick={() => setToevoegen(true)}>+ Tijd</button>}
      </div>
      {vast && <div className="paneel vlak klein">Deze week is goedgekeurd en ligt vast. Klopt er iets niet, vraag dan je projectmanager om hem te heropenen.</div>}
      <div style={{ overflowX: 'auto' }}>
        <table className="tabel weekstaat">
          <thead>
            <tr>
              <th>Waaraan</th>
              {w.dagen.map((d) => <th key={d} className="rechts" style={{ color: d === vandaag() ? 'var(--merk)' : undefined }}>{fmtDag(d)}</th>)}
              <th className="rechts">Totaal</th>
            </tr>
          </thead>
          <tbody>
            {rijen.map((r) => (
              <tr key={r.sleutel}>
                <td>
                  <b>{r.label}</b>
                  <div className="klein mute">{SOORT_LABEL[r.soort]} · {r.projectNummer} {r.projectNaam}</div>
                </td>
                {w.dagen.map((d) => (
                  <td key={d} className="rechts" style={{ width: 84 }}>
                    <Cel waarde={som(r.sleutel, d)} uit={vast} onOpslaan={(t) => opslaan(r, d, t)} />
                  </td>
                ))}
                <td className="rechts vet">{fmtDuur(som(r.sleutel, null))}</td>
              </tr>
            ))}
            {!rijen.length && <tr><td colSpan={7} className="leeg">Nog niets deze week. Start een timer, voeg tijd toe of haal afspraken uit je agenda.</td></tr>}
          </tbody>
          <tfoot>
            <tr>
              <td className="vet">Totaal</td>
              {w.dagen.map((d) => {
                const g = som(null, d);
                const b = (w.beschikbaar[d] ?? 0) * 60;
                return <td key={d} className="rechts"><b style={{ color: b && g < b ? 'var(--oranje)' : undefined }}>{fmtDuur(g)}</b><div className="klein mute">van {fmtDuur(b)}</div></td>;
              })}
              <td className="rechts vet">{fmtDuur(totaal)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      {w.regels.length > 0 && (
        <details>
          <summary className="klein">Alle regels deze week ({w.regels.length})</summary>
          <table className="tabel" style={{ marginTop: 8 }}>
            <thead><tr><th>Datum</th><th>Waaraan</th><th>Omschrijving</th><th className="rechts">Duur</th><th>Bron</th><th>Facturabel</th><th /></tr></thead>
            <tbody>
              {w.regels.map((r) => (
                <tr key={r.id}>
                  <td className="nowrap">{fmtDag(r.datum)}</td>
                  <td>{rijen.find((x) => x.sleutel === r.sleutel)?.label}</td>
                  <td className="klein">{r.omschrijving}</td>
                  <td className="rechts">{r.eind ? fmtDuur(r.minuten) : <Badge toon="oranje">loopt</Badge>}</td>
                  <td className="klein mute">{BRON[r.bron]}</td>
                  <td><input type="checkbox" checked={r.facturabel} disabled={vast} onChange={() => factuur.mutate(r)} /></td>
                  <td>{!vast && <VerwijderKnop klein label="🗑" onBevestig={() => verwijder.mutate(r.id)} />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
      {kies && (
        <Modal titel="Rij toevoegen" onSluit={() => setKies(false)}>
          <DoelZoeker engineerId={engineerId} onKies={(d) => { setExtra([...extra, { ...d, sleutel: `${d.soort}:${d.id}` }]); setKies(false); }} />
        </Modal>
      )}
      {toevoegen && <TijdToevoegen engineerId={engineerId} onSluit={() => setToevoegen(false)} />}
    </div>
  );
}

function Cel({ waarde, uit, onOpslaan }: { waarde: number; uit: boolean; onOpslaan: (t: string) => void }) {
  const [t, setT] = useState(waarde ? fmtDuur(waarde) : '');
  useEffect(() => setT(waarde ? fmtDuur(waarde) : ''), [waarde]);
  return (
    <input
      value={t}
      disabled={uit}
      onChange={(e) => setT(e.target.value)}
      onBlur={() => onOpslaan(t)}
      onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
      placeholder="–"
      style={{ textAlign: 'right', width: 72, padding: '5px 7px' }}
    />
  );
}

interface Afspraak { id: string; datum: string; van: string; tot: string; onderwerp: string; minuten: number; voorstel: Doel | null; geschreven: boolean }

/** SPEC §11.7 – afspraken uit de gekoppelde agenda (Outlook of Google) met één klik als uren. */
function UitAgenda({ maandag }: { maandag: string }) {
  const ververs = useVerversUren();
  const q = useQuery({ queryKey: ['agenda-uren', maandag], queryFn: () => api.get<{ gekoppeld: boolean; afspraken: Afspraak[] }>(`/api/tijd/agenda?maandag=${maandag}`) });
  const [keuze, setKeuze] = useState<Record<string, Doel>>({});
  const [factuur, setFactuur] = useState<Record<string, boolean>>({});
  const [kiesVoor, setKiesVoor] = useState<Afspraak | null>(null);
  const schrijf = useActie((a: Afspraak) => {
    const d = keuze[a.id] ?? a.voorstel!;
    return api.post('/api/tijd/agenda', { afspraakId: a.id, datum: a.datum, minuten: a.minuten, onderwerp: a.onderwerp, doel: { soort: d.soort, id: d.id }, facturabel: factuur[a.id] ?? false });
  }, { succes: 'Afspraak als uren geschreven', opNa: ververs });
  if (!q.data) return <Laden />;
  if (!q.data.gekoppeld) return <div className="leeg">Je agenda is nog niet gekoppeld. Koppel hem via het menu rechtsboven › <b>Mijn agenda koppelen</b> (Outlook of Google); daarna zie je hier je afspraken.</div>;
  return (
    <div className="kolom" style={{ gap: 8 }}>
      <p className="klein mute" style={{ margin: 0 }}>Afspraken uit je agenda deze week. Kies waar de tijd bij hoort en schrijf hem met één klik. Staat <b>[projectnummer]</b> in het onderwerp, dan is het project al gekozen.</p>
      <table className="tabel">
        <thead><tr><th>Wanneer</th><th>Afspraak</th><th>Schrijven op</th><th>Facturabel</th><th /></tr></thead>
        <tbody>
          {q.data.afspraken.map((a) => {
            const d = keuze[a.id] ?? a.voorstel;
            return (
              <tr key={a.id}>
                <td className="nowrap">{fmtDag(a.datum)} {a.van}–{a.tot}</td>
                <td>{a.onderwerp}</td>
                <td>{a.geschreven ? <span className="mute">–</span> : <button className="knop klein" onClick={() => setKiesVoor(a)}>{d ? `${d.projectNummer ?? ''} ${d.label}` : 'Kies…'}</button>}</td>
                <td>{!a.geschreven && <input type="checkbox" checked={factuur[a.id] ?? false} onChange={(e) => setFactuur({ ...factuur, [a.id]: e.target.checked })} />}</td>
                <td className="rechts nowrap">{a.geschreven ? <Badge toon="groen">✓ geschreven</Badge> : <button className="knop merk klein" disabled={!d || schrijf.isPending} onClick={() => schrijf.mutate(a)}>Schrijf {fmtDuur(a.minuten)}</button>}</td>
              </tr>
            );
          })}
          {!q.data.afspraken.length && <tr><td colSpan={5} className="leeg">Geen afspraken deze week.</td></tr>}
        </tbody>
      </table>
      {kiesVoor && (
        <Modal titel={`Schrijven op… (${kiesVoor.onderwerp})`} onSluit={() => setKiesVoor(null)}>
          <DoelZoeker onKies={(d) => { setKeuze({ ...keuze, [kiesVoor.id]: d }); setKiesVoor(null); }} />
        </Modal>
      )}
    </div>
  );
}

/** SPEC §11.7 – persoonlijke sleutel voor de Outlook-invoegtoepassing. */
function OutlookSleutels() {
  const q = useQuery({ queryKey: ['sleutels'], queryFn: () => api.get<{ id: string; naam: string; laatstGebruikt: string | null; aangemaakt: string }[]>('/api/tijd/sleutels') });
  const [nieuw, setNieuw] = useState<string | null>(null);
  const maak = useActie(() => api.post<{ token: string }>('/api/tijd/sleutels', { naam: 'Outlook' }), { opNa: (r) => { setNieuw(r.token); void q.refetch(); } });
  const weg = useActie((id: string) => api.del(`/api/tijd/sleutels/${id}`), { succes: 'Sleutel ingetrokken', opNa: () => void q.refetch() });
  const manifest = `${window.location.origin}/outlook/manifest.xml`;
  return (
    <div className="kolom" style={{ gap: 12 }}>
      <p style={{ margin: 0 }}>Met de invoegtoepassing schrijf je uren vanuit Outlook: open een afspraak, kies het project en schrijf de tijd van de afspraak, of start een timer.</p>
      <ol style={{ margin: 0, paddingLeft: 18 }} className="kolom">
        <li>Maak hieronder een sleutel en kopieer hem.</li>
        <li>Outlook (web of nieuw Outlook): <b>Apps › Invoegtoepassingen ophalen › Mijn invoegtoepassingen › Aangepaste invoegtoepassing toevoegen › Toevoegen vanaf URL</b> en plak <code>{manifest}</code>.</li>
        <li>Open een afspraak, klik op <b>bsmartr uren</b> en plak de sleutel (eenmalig).</li>
      </ol>
      <p className="klein mute" style={{ margin: 0 }}>Google Agenda heeft geen vergelijkbare invoegtoepassing; gebruik daar het tabblad <i>Uit je agenda</i>. Je beheerder kan de invoegtoepassing ook voor iedereen tegelijk uitrollen via het Microsoft 365-beheercentrum.</p>
      {nieuw && (
        <div className="paneel vlak kolom" style={{ background: 'var(--merk-bg)', gap: 6 }}>
          <b>Je nieuwe sleutel (alleen nu zichtbaar):</b>
          <div className="rij"><code className="groei" style={{ wordBreak: 'break-all' }}>{nieuw}</code><button className="knop klein" onClick={() => { void navigator.clipboard.writeText(nieuw); toast('Gekopieerd', 'succes'); }}>Kopieer</button></div>
          <span className="klein mute">Deel hem met niemand; hij geeft toegang tot jouw uren.</span>
        </div>
      )}
      <div className="rij"><button className="knop merk" onClick={() => maak.mutate()}>Nieuwe sleutel maken</button></div>
      {q.data && q.data.length > 0 && (
        <table className="tabel">
          <thead><tr><th>Naam</th><th>Gemaakt</th><th>Laatst gebruikt</th><th /></tr></thead>
          <tbody>
            {q.data.map((k) => (
              <tr key={k.id}><td>{k.naam}</td><td>{fmtDag(k.aangemaakt.slice(0, 10), 'jaar')}</td><td>{k.laatstGebruikt ? new Date(k.laatstGebruikt).toLocaleString('nl-NL') : '–'}</td><td><VerwijderKnop klein label="Intrekken" onBevestig={() => weg.mutate(k.id)} /></td></tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
